import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Users, 
  TrendingUp, 
  Package, 
  CreditCard,
  BarChart3,
  MessageSquare,
  Camera,
  Shield,
  Globe,
  Headphones,
  CheckCircle,
  ArrowRight,
  ChevronRight,
  Mail,
  Facebook,
  Instagram,
  Phone,
  MapPin,
  Wine,
  Star,
  Menu,
  X
} from "lucide-react";

const ProducersPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToRegister = () => {
    window.location.href = "/#register-form";
  };

  const mainFeatures = [
    {
      icon: Globe,
      title: "Alcance Nacional",
      description: "Expanda seu mercado para além da sua região. Alcance consumidores apaixonados por vinhos artesanais em todo o Portugal.",
      highlight: "Milhares de potenciais clientes"
    },
    {
      icon: Package,
      title: "Gestão de Pedidos",
      description: "Painel administrativo completo e intuitivo para gerir todos os seus pedidos, desde a confirmação até a entrega.",
      highlight: "100% digitalizado"
    },
    {
      icon: BarChart3,
      title: "Relatórios Detalhados",
      description: "Acompanhe suas vendas, produtos mais vendidos e tendências do mercado com relatórios completos e fáceis de entender.",
      highlight: "Dados em tempo real"
    },
    {
      icon: CreditCard,
      title: "Pagamentos Seguros",
      description: "Receba pagamentos de forma segura e rápida. Transferências automáticas para sua conta com total transparência.",
      highlight: "Sem complicações"
    }
  ];

  const additionalFeatures = [
    {
      icon: Camera,
      title: "Marketing Profissional",
      description: "Sua marca divulgada com fotos profissionais e descrições otimizadas para atrair mais clientes."
    },
    {
      icon: MessageSquare,
      title: "Comunicação Direta",
      description: "Chat integrado para comunicar-se diretamente com seus clientes e construir relacionamentos."
    },
    {
      icon: Shield,
      title: "Proteção Garantida",
      description: "Cobertura contra fraudes e problemas de entrega. Venda com total tranquilidade."
    },
    {
      icon: Headphones,
      title: "Suporte Dedicado",
      description: "Equipe de suporte especializada para ajudá-lo em todas as etapas do processo."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Cadastre-se",
      description: "Preencha o formulário com as informações da sua vinícola ou quinta. É rápido e gratuito."
    },
    {
      number: "02",
      title: "Validação",
      description: "Nossa equipe irá validar suas informações e entrar em contato para conhecer melhor seus produtos."
    },
    {
      number: "03",
      title: "Configure sua Loja",
      description: "Adicione seus vinhos, fotos, descrições e defina seus preços no painel do produtor."
    },
    {
      number: "04",
      title: "Comece a Vender",
      description: "Pronto! Seus vinhos estarão disponíveis para milhares de consumidores em todo o país."
    }
  ];

  const testimonials = [
    {
      name: "António Silva",
      role: "Quinta do Vale, Douro",
      text: "O Caseirinho transformou nosso negócio. Agora alcançamos clientes em todo Portugal sem sair da nossa quinta.",
      rating: 5
    },
    {
      name: "Maria Santos",
      role: "Adega Familiar, Alentejo",
      text: "A plataforma é muito fácil de usar e o suporte é excelente. Nossas vendas triplicaram em 6 meses.",
      rating: 5
    },
    {
      name: "João Ferreira",
      role: "Vinhos do Dão",
      text: "Finalmente uma plataforma que valoriza os pequenos produtores. Recomendo a todos os colegas.",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "Quanto custa para ser parceiro?",
      answer: "O cadastro é totalmente gratuito. Cobramos apenas uma pequena comissão sobre as vendas realizadas."
    },
    {
      question: "Quem pode se cadastrar?",
      answer: "Qualquer produtor de vinhos artesanais em Portugal pode se cadastrar, desde pequenas quintas familiares até cooperativas."
    },
    {
      question: "Como funcionam os pagamentos?",
      answer: "Os pagamentos são processados de forma segura e transferidos para sua conta bancária semanalmente."
    },
    {
      question: "Preciso de experiência com tecnologia?",
      answer: "Não! Nossa plataforma foi desenhada para ser simples e intuitiva. Oferecemos treinamento completo."
    }
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Lato', sans-serif" }}>
      {/* Header/Navbar */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_wine-marketplace-7/artifacts/q5fe9prt_logo1_ocaseirinho.png"
                alt="O Caseirinho"
                className="h-16 w-auto"
              />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link 
                to="/"
                className="text-[#722F37] hover:text-[#5a232b] transition-colors text-sm font-medium"
              >
                Início
              </Link>
              <Link 
                to="/produtores"
                className="text-[#722F37] hover:text-[#5a232b] transition-colors text-sm font-medium border-b-2 border-[#722F37]"
              >
                Produtores
              </Link>
              <Link 
                to="/beneficios"
                className="text-[#722F37] hover:text-[#5a232b] transition-colors text-sm font-medium"
              >
                Benefícios
              </Link>
              <button 
                onClick={scrollToRegister}
                className="px-5 py-2 rounded text-sm font-medium transition-colors"
                style={{
                  backgroundColor: '#722F37',
                  color: '#FFFFFF'
                }}
              >
                Cadastre-se
              </button>
            </nav>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-[#722F37] p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col gap-3">
                <Link 
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#722F37] hover:text-[#5a232b] transition-colors text-left py-2"
                >
                  Início
                </Link>
                <Link 
                  to="/produtores"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#722F37] hover:text-[#5a232b] transition-colors text-left py-2 font-semibold"
                >
                  Produtores
                </Link>
                <Link 
                  to="/beneficios"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#722F37] hover:text-[#5a232b] transition-colors text-left py-2"
                >
                  Benefícios
                </Link>
                <button 
                  onClick={() => { scrollToRegister(); setMobileMenuOpen(false); }}
                  className="px-5 py-2 rounded text-sm font-medium transition-colors text-center mt-2"
                  style={{
                    backgroundColor: '#722F37',
                    color: '#FFFFFF'
                  }}
                >
                  Cadastre-se
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-24 overflow-hidden"
        style={{
          backgroundColor: '#722F37'
        }}
      >
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span 
                className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}
              >
                Para Produtores
              </span>
              
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
              >
                Leve Seus Vinhos
                <br />
                <span style={{ color: '#D4AF37' }}>a Todo Portugal</span>
              </h1>
              
              <p 
                className="text-lg md:text-xl mb-8 leading-relaxed"
                style={{ color: 'rgba(245, 237, 224, 0.9)' }}
              >
                Junte-se a uma comunidade de produtores artesanais e alcance 
                milhares de consumidores apaixonados por vinhos de qualidade. 
                Sem complicações, sem burocracia.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={scrollToRegister}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
                  style={{
                    backgroundColor: '#D4AF37',
                    color: '#722F37'
                  }}
                >
                  Quero ser Parceiro
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div 
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{ border: '4px solid rgba(212, 175, 55, 0.5)' }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_728153dc-96ca-4c27-a962-b3dd550d3ec5/artifacts/kb4oajh6_vinhos.png"
                  alt="Vinhos O Caseirinho"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        className="py-12"
        style={{ backgroundColor: '#F5EEE0' }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p 
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: '#722F37', fontFamily: "'Playfair Display', serif" }}
              >
                50+
              </p>
              <p style={{ color: '#5D4037' }}>Produtores Parceiros</p>
            </div>
            <div className="text-center">
              <p 
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: '#722F37', fontFamily: "'Playfair Display', serif" }}
              >
                1000+
              </p>
              <p style={{ color: '#5D4037' }}>Vinhos Disponíveis</p>
            </div>
            <div className="text-center">
              <p 
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: '#722F37', fontFamily: "'Playfair Display', serif" }}
              >
                15+
              </p>
              <p style={{ color: '#5D4037' }}>Regiões de Portugal</p>
            </div>
            <div className="text-center">
              <p 
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: '#722F37', fontFamily: "'Playfair Display', serif" }}
              >
                98%
              </p>
              <p style={{ color: '#5D4037' }}>Satisfação dos Parceiros</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span 
              className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: 'rgba(114, 47, 55, 0.1)', color: '#722F37' }}
            >
              Ferramentas Poderosas
            </span>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                color: '#722F37'
              }}
            >
              Tudo o Que Você Precisa
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#5D4037' }}
            >
              Ferramentas completas para gerir e expandir seu negócio de vinhos artesanais
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mainFeatures.map((feature, index) => (
              <div 
                key={index}
                className="flex gap-6 p-6 rounded-2xl transition-all duration-300 hover:shadow-lg"
                style={{ 
                  backgroundColor: '#F5EEE0',
                  border: '1px solid rgba(114, 47, 55, 0.1)'
                }}
              >
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#722F37' }}
                >
                  <feature.icon className="w-8 h-8" style={{ color: '#D4AF37' }} />
                </div>
                
                <div>
                  <h3 
                    className="text-xl font-bold mb-2"
                    style={{ 
                      fontFamily: "'Playfair Display', serif",
                      color: '#722F37'
                    }}
                  >
                    {feature.title}
                  </h3>
                  
                  <p 
                    className="mb-3 leading-relaxed"
                    style={{ color: '#5D4037' }}
                  >
                    {feature.description}
                  </p>
                  
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', color: '#8B6914' }}
                  >
                    {feature.highlight}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section 
        className="py-20"
        style={{ backgroundColor: '#722F37' }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                color: '#F5EDE0'
              }}
            >
              E Ainda Mais Vantagens
            </h2>
            <div 
              className="w-24 h-1 mx-auto"
              style={{ backgroundColor: '#D4AF37' }}
            ></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl transition-all duration-300 hover:transform hover:scale-105"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(212, 175, 55, 0.2)'
                }}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)' }}
                >
                  <feature.icon className="w-7 h-7" style={{ color: '#D4AF37' }} />
                </div>
                
                <h3 
                  className="text-lg font-bold mb-2"
                  style={{ 
                    fontFamily: "'Playfair Display', serif",
                    color: '#F5EDE0'
                  }}
                >
                  {feature.title}
                </h3>
                
                <p 
                  className="text-sm"
                  style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20" style={{ backgroundColor: '#F5EEE0' }}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                color: '#722F37'
              }}
            >
              Perguntas Frequentes
            </h2>
            <div 
              className="w-24 h-1 mx-auto"
              style={{ backgroundColor: '#D4AF37' }}
            ></div>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6"
                style={{ border: '1px solid rgba(114, 47, 55, 0.1)' }}
              >
                <h3 
                  className="text-lg font-bold mb-2 flex items-start gap-3"
                  style={{ color: '#722F37' }}
                >
                  <CheckCircle className="w-6 h-6 flex-shrink-0" style={{ color: '#D4AF37' }} />
                  {faq.question}
                </h3>
                <p 
                  className="ml-9"
                  style={{ color: '#5D4037' }}
                >
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20"
        style={{
          background: 'linear-gradient(135deg, #722F37 0%, #5D2E35 100%)'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Wine className="w-16 h-16 mx-auto mb-6" style={{ color: '#D4AF37' }} />
          
          <h2 
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{ 
              fontFamily: "'Playfair Display', serif",
              color: '#F5EDE0'
            }}
          >
            Pronto para Expandir Seu Negócio?
          </h2>
          
          <p 
            className="text-lg mb-10 max-w-2xl mx-auto"
            style={{ color: 'rgba(245, 237, 224, 0.9)' }}
          >
            Junte-se aos produtores que já estão transformando suas vendas 
            com o Caseirinho. O cadastro é gratuito e leva apenas 5 minutos.
          </p>
          
          <button 
            onClick={scrollToRegister}
            className="inline-flex items-center gap-2 px-10 py-5 rounded-lg text-xl font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
            style={{
              backgroundColor: '#D4AF37',
              color: '#722F37'
            }}
          >
            Cadastrar Minha Vinícola
            <ArrowRight className="w-6 h-6" />
          </button>
          
          <p 
            className="mt-6 text-sm"
            style={{ color: 'rgba(245, 237, 224, 0.6)' }}
          >
            Sem taxas de adesão • Suporte completo • Comece a vender em dias
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="pt-16 pb-8"
        style={{
          backgroundColor: '#722F37',
          borderTop: '4px solid #D4AF37'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            
            {/* Column 1 - About/Logo */}
            <div className="lg:col-span-1">
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_728153dc-96ca-4c27-a962-b3dd550d3ec5/artifacts/c8drzsa3_logo3_dark_badge-removebg-preview.png"
                  alt="O Caseirinho - Produtos Artesanais"
                  className="h-24 w-auto"
                />
              </div>
              <p 
                className="text-sm leading-relaxed mb-6"
                style={{ color: 'rgba(245, 237, 224, 0.8)' }}
              >
                Conectamos apaixonados por vinho a produtores artesanais de todo o país. Qualidade e tradição em cada garrafa.
              </p>
              <div className="flex items-center gap-3">
                <a 
                  href="https://facebook.com/caseirinho"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
                >
                  <Facebook className="w-5 h-5" style={{ color: '#D4AF37' }} />
                </a>
                <a 
                  href="https://instagram.com/caseirinho"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)' }}
                >
                  <Instagram className="w-5 h-5" style={{ color: '#D4AF37' }} />
                </a>
              </div>
            </div>

            {/* Column 2 - Quick Links */}
            <div>
              <h4 
                className="text-lg font-semibold mb-5"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
              >
                Links Rápidos
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link 
                    to="/"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Início
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/produtores"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Para Produtores
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/beneficios"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Benefícios
                  </Link>
                </li>
                <li>
                  <a 
                    href="/#register-form"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <ChevronRight className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    Registrar
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3 - Contact */}
            <div>
              <h4 
                className="text-lg font-semibold mb-5"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
              >
                Contacto
              </h4>
              <ul className="space-y-4">
                <li>
                  <a 
                    href="mailto:info@caseirinho.pt"
                    className="flex items-start gap-3 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
                    <span>info@caseirinho.pt</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="tel:+351912345678"
                    className="flex items-start gap-3 text-sm transition-colors duration-200 hover:text-[#D4AF37]"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
                    <span>+351 912 345 678</span>
                  </a>
                </li>
                <li>
                  <div 
                    className="flex items-start gap-3 text-sm"
                    style={{ color: 'rgba(245, 237, 224, 0.8)' }}
                  >
                    <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
                    <span>Lisboa, Portugal</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Column 4 - CTA */}
            <div>
              <h4 
                className="text-lg font-semibold mb-5"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#F5EDE0'
                }}
              >
                Seja um Parceiro
              </h4>
              <p 
                className="text-sm mb-4"
                style={{ color: 'rgba(245, 237, 224, 0.8)' }}
              >
                Cadastre sua vinícola e comece a vender para todo o país.
              </p>
              <button 
                onClick={scrollToRegister}
                className="w-full py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 hover:shadow-lg"
                style={{
                  backgroundColor: '#D4AF37',
                  color: '#722F37'
                }}
              >
                Cadastrar Agora
              </button>
              
              <div className="flex items-center gap-2 mt-6">
                <Wine className="w-5 h-5" style={{ color: '#D4AF37' }} />
                <span 
                  className="text-xs"
                  style={{ color: 'rgba(245, 237, 224, 0.6)' }}
                >
                  100% Vinhos Artesanais
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div 
            className="w-full h-[1px] mb-8"
            style={{ backgroundColor: 'rgba(212, 175, 55, 0.3)' }}
          ></div>

          {/* Bottom Footer */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p 
              className="text-sm"
              style={{ color: 'rgba(245, 237, 224, 0.6)' }}
            >
              © {new Date().getFullYear()} O Caseirinho. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <a 
                href="#"
                className="text-xs transition-colors duration-200 hover:text-[#D4AF37]"
                style={{ color: 'rgba(245, 237, 224, 0.6)' }}
              >
                Termos de Uso
              </a>
              <a 
                href="#"
                className="text-xs transition-colors duration-200 hover:text-[#D4AF37]"
                style={{ color: 'rgba(245, 237, 224, 0.6)' }}
              >
                Política de Privacidade
              </a>
              <a 
                href="#"
                className="text-xs transition-colors duration-200 hover:text-[#D4AF37]"
                style={{ color: 'rgba(245, 237, 224, 0.6)' }}
              >
                Cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProducersPage;
